import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Avatar, Box, Stack, Typography } from "@mui/material";

import { EVOTRUX_BLUE, EVOTRUX_BLUE_LIGHT } from "constants/index";
import { getInitialsFromName } from "utils";

import { pubnubTimeTokenToRelative } from "./utils";

import type { InboxChat } from "context/ChatUnreadContext";

const AVATAR_SIZE = 36;
const UNREAD_DOT_SIZE = 10;

interface ChatInboxRowProps {
  row: InboxChat;
  onClick: (row: InboxChat) => void;
}

export function ChatInboxRow(props: ChatInboxRowProps) {
  const { row, onClick } = props;
  const isUnread = row.unread_count > 0;
  const relativeTime = pubnubTimeTokenToRelative(row.latest_message_time_token);
  const initials = getInitialsFromName(row.latest_message_sender_name);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={() => onClick(row)}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          event.preventDefault();
          onClick(row);
        }
      }}
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        px: 2,
        py: 1.5,
        cursor: "pointer",
        borderBottom: 1,
        borderColor: "divider",
        transition: "background-color 0.15s",
        "&:hover": { bgcolor: "grey.50" },
        "&:hover .ChatInboxRow-chevron": { opacity: 1 },
        "&:focus-visible": {
          outline: "2px solid",
          outlineColor: "primary.main",
          outlineOffset: -2,
        },
      }}
    >
      <Avatar
        sx={{
          width: AVATAR_SIZE,
          height: AVATAR_SIZE,
          mt: 0.5,
          bgcolor: EVOTRUX_BLUE_LIGHT,
          color: EVOTRUX_BLUE,
          fontSize: 14,
          fontWeight: 600,
        }}
      >
        {initials}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0, pr: 3 }}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="baseline"
          spacing={1}
        >
          <Typography
            variant="body2"
            noWrap
            sx={{ fontWeight: isUnread ? 700 : 500, color: "text.primary" }}
          >
            {row.shipment_label}
          </Typography>
          {relativeTime && (
            <Typography
              variant="caption"
              sx={{
                flexShrink: 0,
                fontWeight: isUnread ? 700 : 400,
                color: isUnread ? "primary.main" : "text.secondary",
              }}
            >
              {relativeTime}
            </Typography>
          )}
        </Stack>

        {row.counterparty_company_name && (
          <Typography
            variant="caption"
            noWrap
            sx={{
              display: "block",
              mb: 0.25,
              fontWeight: isUnread ? 600 : 400,
              color: "text.secondary",
            }}
          >
            {row.counterparty_company_name}
          </Typography>
        )}

        <Typography
          variant="body2"
          noWrap
          sx={{
            mb: 0.5,
            fontWeight: isUnread ? 700 : 400,
            color: isUnread ? "text.primary" : "text.secondary",
          }}
        >
          {row.latest_message_sender_name}
        </Typography>

        <Typography
          variant="body2"
          sx={{
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            fontWeight: isUnread ? 600 : 400,
            fontStyle: row.latest_message_preview ? "normal" : "italic",
            color: isUnread ? "text.primary" : "text.secondary",
          }}
        >
          {row.latest_message_preview ||
            "No messages sent yet. Click to send a message."}
        </Typography>
      </Box>

      <Stack
        sx={{
          position: "absolute",
          right: 12,
          top: "50%",
          transform: "translateY(-50%)",
        }}
        alignItems="center"
        spacing={1}
      >
        {isUnread && (
          <Box
            sx={{
              width: UNREAD_DOT_SIZE,
              height: UNREAD_DOT_SIZE,
              borderRadius: "50%",
              bgcolor: "primary.main",
            }}
          />
        )}
        <ChevronRightIcon
          className="ChatInboxRow-chevron"
          fontSize="small"
          sx={{
            color: "text.disabled",
            opacity: 0,
            transition: "opacity 0.15s",
          }}
        />
      </Stack>
    </Box>
  );
}
